import { useFormik } from 'formik'
import { useState, useContext } from 'react'
import * as Yup from 'yup'
import { TemplateContext } from '~/components/Template/context'
import masks from '~/themes/masks'
import { mapToOptions } from './helpers/mapToOptions'
import { postCourse } from '~/screens/courses/requests'
import { getV4MacroAreas, getV4MicroAreas } from 'services/api'
import { toast } from 'react-toastify'

export const useAddCourse = ({ setIsLoading }) => {
  const { goToPage, accessData } = useContext(TemplateContext)
  const regexOnlyNumbers = /^[0-9]*$/
  const formik = useFormik({
    initialValues: {
      courseName: '',
      courseArea: null,
      courseSpecialty: '',
      courseValue: '',
      courseValueFormatted: '',
      courseWorkload: '',
      courseDisclaimer: true,
      courseDescription: '',
      hasSelectionProcess: false,
    },
    onSubmit: async ({
      courseArea,
      courseDescription,
      courseName,
      courseDisclaimer,
      courseSpecialty,
      courseValue,
      courseWorkload,
      hasSelectionProcess,
    }) => {
      const courseBody = {
        name: courseName.trim(),
        price: parseFloat(courseValue) / 100,
        priceInCents: Number(courseValue),
        MacroAreaId: courseArea.id,
        MicroAreaId: courseSpecialty.id,
        workload: parseInt(courseWorkload) || 0,
        visible: courseDisclaimer,
        hasSelectionProcess,
        ...(courseDescription ? { description: courseDescription?.trim() } : {}),
      }

      createCourse(courseBody)
    },
    validationSchema: Yup.object().shape({
      courseName: Yup.string()
        .trim()
        .min(8, 'O curso deve ter no mínimo 8 caracteres.')
        .required('O nome do curso é obrigatório.'),
      courseDescription: Yup.string()
        .trim()
        .min(25, 'A descrição do curso deve ter no mínimo 25 caracteres.')
        .required('A descrição do curso é obrigatória'),
      courseSpecialty: Yup.string().required('A especialidade é obrigatória'),
      courseValue: Yup.number()
        .min(3000, 'O preço do curso deve ser de mínimo R$ 30,00')
        .required('O preço do curso é obrigatório'),
      courseWorkload: Yup.number().min(0).required('A carga horária do curso é obrigatória'),
      courseDisclaimer: Yup.boolean(),
    }),
    validate: (values) => {
      let errors = {}
      const { hasProductValueLimit = true } = accessData

      if (values.courseName !== '' && values.courseName.match(regexOnlyNumbers)) {
        errors.courseName = 'O nome não pode conter apenas números'
      }

      if (!accessData) return errors

      if (values.courseValue > 6000000 && hasProductValueLimit) {
        errors.courseValue = 'O valor do curso não pode exceder R$ 60.000,00'
      }

      return errors
    },
    validateOnMount: false,
    validateOnChange: true,
    validateOnBlur: true,
  })

  const [courseAreaOptions, setCourseAreaOptions] = useState([])
  const [courseSpecialtyOptions, setCourseSpecialtyOptions] = useState([])

  function handleResetForm() {
    formik.resetForm()
    goToPage('cursos')
  }

  const createCourse = async (formObj) => {
    try {
      setIsLoading(true)
      const response = await postCourse(formObj)

      if (response.status > 199 && response.status < 300) {
        toast('Curso cadastrado com sucesso!')
        setTimeout(() => {
          goToPage('cursos')
        }, 1500)
      }
    } catch (error) {
      toast(
        error.response?.data?.error?.message ||
          error.response?.data?.errors?.[0]?.message ||
          'Algo de errado aconteceu criando o seu curso 😔 Por favor, tente novamente.',
      )
    } finally {
      setIsLoading(false)
    }
  }

  const hasErrors = !!Object.keys(formik.errors).length

  const setCourseArea = async () => {
    const response = await getV4MacroAreas({ setIsLoading })

    if (!response || !response.metadata || !response.content) return

    const { metadata, content: macroAreas } = response

    const macroAreaOptions = mapToOptions(macroAreas)

    setCourseAreaOptions(macroAreaOptions)

    if (metadata.defaultMacroAreaId) {
      const defaultArea = macroAreaOptions.find((area) => area.id === metadata.defaultMacroAreaId)

      handleCourseArea(defaultArea)
    }
  }

  const handleCourseArea = async (macroArea) => {
    if (macroArea.id !== formik.values.courseArea?.id) {
      formik.setFieldValue('courseArea', macroArea)

      const response = await getV4MicroAreas({ macroAreaId: macroArea.id, setIsLoading })

      if (!response || !response.metadata || !response.content) return

      const { content: microAreas } = response

      const microAreaOptions = mapToOptions(microAreas)

      setCourseSpecialtyOptions(microAreaOptions)

      formik.setFieldValue('courseSpecialty', null)
    }
  }

  const handleMicroArea = (microArea) => {
    formik.setFieldValue('courseSpecialty', microArea)
  }

  const handleMaskInputChange = async (e) => {
    const { value } = e.target

    formik.setFieldValue('courseValueFormatted', masks.currency(value))

    formik.setFieldValue('courseValue', masks.unmask(value))

    await formik.validateField('courseValue')
  }

  return {
    formik,
    hasErrors,
    courseAreaOptions,
    courseSpecialtyOptions,
    setCourseArea,
    handleCourseArea,
    handleMicroArea,
    handleMaskInputChange,
    handleResetForm,
  }
}
